import { Permission, UserPermission } from "./types";

/**
 * Canonical permission tree for all apps. Labels are i18n keys in the
 * `common` namespace, under `userManagement.permissions`.
 * - `labelKey`       – full display name used in the user-page permission panel.
 * - `shortLabelKey`  – abbreviated name used in compact UIs (e.g. CSV upload DataGrid headers).
 * - `descriptionKey` – optional explanatory text shown below the permission toggle.
 */
export const APP_PERMISSION_TREE: UserPermission[] = [
  {
    permission: Permission.ALERT,
    labelKey: "userManagement.permissions.alert.label",
    shortLabelKey: "userManagement.permissions.alert.shortLabel",
    subPermissions: [
      {
        permission: Permission.ALERT_CREATE_BULLETIN,
        labelKey: "userManagement.permissions.alert.createBulletin.label",
      },
      {
        permission: Permission.ALERT_DASHBOARD,
        labelKey: "userManagement.permissions.alert.dashboard.label",
        shortLabelKey: "userManagement.permissions.alert.dashboard.shortLabel",
      },
    ],
  },
  {
    permission: Permission.REPORT,
    labelKey: "userManagement.permissions.report.label",
  },
  {
    permission: Permission.REPORT_V2,
    labelKey: "userManagement.permissions.reportV2.label",
    shortLabelKey: "userManagement.permissions.reportV2.shortLabel",
    subPermissions: [
      {
        permission: Permission.REPORT_V2_CREATE_SURVEY,
        labelKey: "userManagement.permissions.reportV2.createSurvey.label",
        shortLabelKey:
          "userManagement.permissions.reportV2.createSurvey.shortLabel",
      },
      {
        permission: Permission.REPORT_V2_VIEW_REPORTS,
        labelKey: "userManagement.permissions.reportV2.viewReports.label",
      },
      {
        permission: Permission.REPORT_V2_VIEW_INDIVIDUAL_REPORT,
        labelKey:
          "userManagement.permissions.reportV2.viewIndividualReport.label",
      },
      {
        permission: Permission.REPORT_V2_VIEW_CITIZEN_ENGAGE,
        labelKey: "userManagement.permissions.reportV2.viewCitizenEngage.label",
        shortLabelKey:
          "userManagement.permissions.reportV2.viewCitizenEngage.shortLabel",
      },
      {
        permission: Permission.REPORT_V2_VIEW_DASHBOARD,
        labelKey: "userManagement.permissions.reportV2.viewDashboard.label",
        shortLabelKey:
          "userManagement.permissions.reportV2.viewDashboard.shortLabel",
      },
      {
        permission: Permission.REPORT_V2_VIEW_SETTINGS,
        labelKey: "userManagement.permissions.reportV2.viewSettings.label",
        shortLabelKey:
          "userManagement.permissions.reportV2.viewSettings.shortLabel",
      },
      {
        permission: Permission.REPORT_V2_UPDATE_REPORTS,
        labelKey: "userManagement.permissions.reportV2.updateReports.label",
      },
    ],
  },
  {
    permission: Permission.REGISTRY,
    labelKey: "userManagement.permissions.registry.label",
    shortLabelKey: "userManagement.permissions.registry.shortLabel",
    subPermissions: [
      {
        permission: Permission.REGISTRY_VIEW_ENTRIES,
        labelKey: "userManagement.permissions.registry.viewEntries.label",
      },
      {
        permission: Permission.REGISTRY_CREATE_ENTRIES,
        labelKey: "userManagement.permissions.registry.createEntries.label",
      },
      {
        permission: Permission.REGISTRY_UPDATE_ENTRIES,
        labelKey: "userManagement.permissions.registry.updateEntries.label",
      },
      {
        permission: Permission.REGISTRY_VIEW_DASHBOARD,
        labelKey: "userManagement.permissions.registry.viewDashboard.label",
        shortLabelKey:
          "userManagement.permissions.registry.viewDashboard.shortLabel",
      },
      {
        permission: Permission.REGISTRY_VIEW_SETTINGS,
        labelKey: "userManagement.permissions.registry.viewSettings.label",
        shortLabelKey:
          "userManagement.permissions.registry.viewSettings.shortLabel",
      },
    ],
  },
  {
    permission: Permission.CAMERA_REGISTRY,
    labelKey: "userManagement.permissions.cameraRegistry.label",
    shortLabelKey: "userManagement.permissions.cameraRegistry.shortLabel",
  },
  {
    permission: Permission.INTEL,
    labelKey: "userManagement.permissions.intel.label",
    shortLabelKey: "userManagement.permissions.intel.shortLabel",
    subPermissions: [
      {
        permission: Permission.INTEL_VIEW_ENTRIES,
        labelKey: "userManagement.permissions.intel.viewEntries.label",
      },
      {
        permission: Permission.INTEL_UPDATE_ENTRIES,
        labelKey: "userManagement.permissions.intel.updateEntries.label",
      },
      {
        permission: Permission.INTEL_DELETE_ENTRIES,
        labelKey: "userManagement.permissions.intel.deleteEntries.label",
      },
      {
        permission: Permission.INTEL_VIEW_DASHBOARD,
        labelKey: "userManagement.permissions.intel.viewDashboard.label",
        shortLabelKey:
          "userManagement.permissions.intel.viewDashboard.shortLabel",
      },
      {
        permission: Permission.INTEL_VIEW_SETTINGS,
        labelKey: "userManagement.permissions.intel.viewSettings.label",
        shortLabelKey:
          "userManagement.permissions.intel.viewSettings.shortLabel",
      },
    ],
  },
  {
    permission: Permission.MESSAGE,
    labelKey: "userManagement.permissions.message.label",
    shortLabelKey: "userManagement.permissions.message.shortLabel",
    subPermissions: [
      {
        permission: Permission.MESSAGE_CONVERSATION_READ,
        labelKey: "userManagement.permissions.message.conversationRead.label",
        shortLabelKey:
          "userManagement.permissions.message.conversationRead.shortLabel",
        descriptionKey:
          "userManagement.permissions.message.conversationRead.description",
      },
      {
        permission: Permission.MESSAGE_CONVERSATION_WRITE,
        labelKey: "userManagement.permissions.message.conversationWrite.label",
        shortLabelKey:
          "userManagement.permissions.message.conversationWrite.shortLabel",
        descriptionKey:
          "userManagement.permissions.message.conversationWrite.description",
      },
      {
        permission: Permission.MESSAGE_TEMPLATE_READ,
        labelKey: "userManagement.permissions.message.templateRead.label",
        shortLabelKey:
          "userManagement.permissions.message.templateRead.shortLabel",
        descriptionKey:
          "userManagement.permissions.message.templateRead.description",
      },
      {
        permission: Permission.MESSAGE_TEMPLATE_WRITE,
        labelKey: "userManagement.permissions.message.templateWrite.label",
        shortLabelKey:
          "userManagement.permissions.message.templateWrite.shortLabel",
        descriptionKey:
          "userManagement.permissions.message.templateWrite.description",
      },
      {
        permission: Permission.MESSAGE_FREEFORM_WRITE,
        labelKey: "userManagement.permissions.message.freeformWrite.label",
        shortLabelKey:
          "userManagement.permissions.message.freeformWrite.shortLabel",
        descriptionKey:
          "userManagement.permissions.message.freeformWrite.description",
      },
      {
        permission: Permission.MESSAGE_VIEW_ALL_HISTORY,
        labelKey: "userManagement.permissions.message.viewAllHistory.label",
        shortLabelKey:
          "userManagement.permissions.message.viewAllHistory.shortLabel",
        descriptionKey:
          "userManagement.permissions.message.viewAllHistory.description",
      },
    ],
  },
  {
    permission: Permission.CONNECT,
    labelKey: "userManagement.permissions.connect.label",
    shortLabelKey: "userManagement.permissions.connect.shortLabel",
    subPermissions: [
      {
        permission: Permission.CONNECT_CREATE_MEETING,
        labelKey: "userManagement.permissions.connect.createMeeting.label",
        shortLabelKey:
          "userManagement.permissions.connect.createMeeting.shortLabel",
        descriptionKey:
          "userManagement.permissions.connect.createMeeting.description",
      },
      {
        permission: Permission.CONNECT_VIEW_MEETINGS,
        labelKey: "userManagement.permissions.connect.viewMeetings.label",
        shortLabelKey:
          "userManagement.permissions.connect.viewMeetings.shortLabel",
        descriptionKey:
          "userManagement.permissions.connect.viewMeetings.description",
      },
      {
        permission: Permission.CONNECT_VIEW_RECORDINGS,
        labelKey: "userManagement.permissions.connect.viewRecordings.label",
        shortLabelKey:
          "userManagement.permissions.connect.viewRecordings.shortLabel",
        descriptionKey:
          "userManagement.permissions.connect.viewRecordings.description",
      },
      {
        permission: Permission.CONNECT_MANAGE_SETTINGS,
        labelKey: "userManagement.permissions.connect.manageSettings.label",
        shortLabelKey:
          "userManagement.permissions.connect.manageSettings.shortLabel",
        descriptionKey:
          "userManagement.permissions.connect.manageSettings.description",
      },
    ],
  },
  {
    permission: Permission.PARTNER,
    labelKey: "userManagement.permissions.partner.label",
    shortLabelKey: "userManagement.permissions.partner.shortLabel",
    subPermissions: [
      {
        permission: Permission.PARTNER_VIEW_REPORTS,
        labelKey: "userManagement.permissions.partner.viewReports.label",
        descriptionKey:
          "userManagement.permissions.partner.viewReports.description",
      },
      {
        permission: Permission.PARTNER_ADD_SUPPLEMENTAL,
        labelKey: "userManagement.permissions.partner.addSupplemental.label",
        shortLabelKey:
          "userManagement.permissions.partner.addSupplemental.shortLabel",
        descriptionKey:
          "userManagement.permissions.partner.addSupplemental.description",
      },
    ],
  },
];

/** Apps whose permissions are only offered to orgs that have the app enabled. */
const ORG_GATED_APPS = new Set<Permission>([Permission.CONNECT]);

/**
 * The tree as offered in the user-management UIs. `Permission.REPORT` is the
 * legacy app with no permission UI of its own.
 */
export const assignableAppPermissions = (
  orgApps: readonly string[],
): UserPermission[] =>
  APP_PERMISSION_TREE.filter(
    (p) =>
      p.permission !== Permission.REPORT &&
      (!ORG_GATED_APPS.has(p.permission) || orgApps.includes(p.permission)),
  );

/** Flat set of every sub-permission key for O(1) lookup. */
export const SUB_PERMISSION_KEYS = new Set<Permission>(
  APP_PERMISSION_TREE.flatMap(
    (a) => a.subPermissions?.map((s) => s.permission) ?? [],
  ),
);

export const getAppDef = (perm: Permission): UserPermission | undefined =>
  APP_PERMISSION_TREE.find((a) => a.permission === perm);

export const getParentDef = (subPerm: Permission): UserPermission | undefined =>
  APP_PERMISSION_TREE.find((a) =>
    a.subPermissions?.some((s) => s.permission === subPerm),
  );
